/**
 * Compléments de l'application (la coque elle-même vit dans `components/shell`) :
 * - VersionGate (Q156–Q157) : bannière de grâce ou écran bloquant ;
 * - MissingFileBoundary : reprise après un fichier de version disparu ;
 * - useOnboardingTip (Q97) : aide contextuelle progressive, contournable ;
 * - PageHelpButton : « Comprendre cette page ».
 */
import { Component, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { OnboardingTipKey } from '@myequip/shared';
import { api } from '../core/api.js';
import { useAuth } from '../core/auth.js';
import {
  applyUpdateNow, forceUpdate, getUpdateSnapshot, isMissingFileError, notifyNavigation,
  recoverFromMissingFile, subscribeToUpdate,
} from '../core/app-update.js';
import { Button, Spinner } from './ui.js';
import { Overlay } from './overlay.js';
import { Banner } from './ui/Feedback.js';

// --- Version gate (Q156–Q157) -----------------------------------------------------------------
export const APP_VERSION = '0.1.0';

export function VersionGate({ children }: { children: ReactNode }): ReactNode {
  // A new release downloaded in the background (core/app-update.ts): applied on
  // the next page change when nothing can be lost, offered here otherwise.
  const update = useSyncExternalStore(subscribeToUpdate, getUpdateSnapshot);
  const { pathname } = useLocation();
  // Signing in or out replaces the whole screen without changing the address:
  // it counts as a page change (what was typed in the sign-in form is not a form in progress).
  const session = useAuth().me?.id;
  // The banner stays pinned while a long form scrolls: the top bars sit under it.
  const banner = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const node = banner.current;
    if (!update.prompt || !node) return undefined;
    const root = document.documentElement;
    const publish = (): void => root.style.setProperty('--update-banner-h', `${node.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(node);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--update-banner-h');
    };
  }, [update.prompt]);
  const firstPage = useRef(true);
  useEffect(() => {
    if (firstPage.current) firstPage.current = false;
    else notifyNavigation();
  }, [pathname, session]);
  const { data } = useQuery({
    queryKey: ['version-gate'],
    queryFn: () => api<{ status: 'ok' | 'grace' | 'blocked'; graceUntil?: string; reason?: string }>(
      `/app-version-gate?platform=web&version=${APP_VERSION}`),
    staleTime: 3_600_000,
    // Hourly, not the application-wide minute: this answer changes with a
    // release decision, and every open device would otherwise ask 60 times an hour.
    refetchInterval: 3_600_000,
    retry: false,
  });
  if (data?.status === 'blocked') {
    return (
      <main className="app-main app-stop">
        <div className="card stack app-stop__card">
          <h2>Mise à jour requise</h2>
          <p>
            {data.reason === 'securite'
              ? 'Une mise à jour de sécurité est obligatoire pour continuer.'
              : 'Cette version n’est plus prise en charge. Rechargez la page pour obtenir la dernière version.'}
          </p>
          {/* Not a plain reload: it would show this same release again while
              the new one is still waiting to be applied. */}
          <Button large onClick={() => { void forceUpdate(); }}>Recharger</Button>
        </div>
      </main>
    );
  }
  return (
    <>
      {data?.status === 'grace' && !update.prompt ? (
        <Banner>
          Une nouvelle version est disponible — mise à jour obligatoire
          {data.graceUntil ? ` avant le ${new Date(data.graceUntil).toLocaleDateString('fr-FR')}` : ''}.
        </Banner>
      ) : null}
      {update.prompt ? (
        <div ref={banner} className="app-update">
          <Banner
            title="Nouvelle version disponible"
            action={{ label: 'Mettre à jour', onClick: applyUpdateNow }}
          >
            {update.draftsKept ? 'Votre saisie en cours est conservée sur cet appareil.' : null}
            {update.draftsKept && update.workAtRisk ? ' ' : null}
            {update.workAtRisk ? 'Terminez d’abord ce que vous êtes en train de faire.' : null}
          </Banner>
        </div>
      ) : null}
      {children}
    </>
  );
}

/**
 * A page whose file is gone from the server (a release replaced it while the
 * app was open): the app reloads once on the new release instead of staying
 * blank. If that already happened a moment ago, the failure is shown plainly.
 * Any other error keeps its usual course.
 */
export class MissingFileBoundary extends Component<
  { children: ReactNode }, { error: unknown; repairing: boolean }
> {
  override state: { error: unknown; repairing: boolean } = { error: undefined, repairing: false };

  static getDerivedStateFromError(error: unknown): { error: unknown } {
    return { error };
  }

  override componentDidCatch(error: unknown): void {
    if (isMissingFileError(error)) this.setState({ repairing: recoverFromMissingFile() });
  }

  override render(): ReactNode {
    const { error, repairing } = this.state;
    if (error === undefined) return this.props.children;
    if (!isMissingFileError(error)) throw error;
    if (repairing) return <Spinner />;
    return (
      <main className="app-main app-stop">
        <div className="card stack app-stop__card" role="alert">
          <h2>Page indisponible</h2>
          <p>
            Cette page n’a pas pu être chargée. Vérifiez votre connexion, puis rechargez
            l’application.
          </p>
          <Button large onClick={() => { void forceUpdate(); }}>Recharger</Button>
        </div>
      </main>
    );
  }
}

// --- Aide contextuelle progressive (Q97) : contournable, jamais un second produit ---------------
/** Un conseil déjà fermé ne revient pas : la fermeture est retenue par le compte. */
export function useOnboardingTip(tipKey: OnboardingTipKey): { visible: boolean; dismiss: () => Promise<void> } {
  const { me, reload } = useAuth();
  const seen = Boolean((me?.onboarding_state as Record<string, unknown> | undefined)?.[tipKey]);
  const [dismissed, setDismissed] = useState(false);
  const dismiss = async (): Promise<void> => {
    setDismissed(true);
    try {
      await api('/auth/onboarding', { method: 'POST', body: { key: tipKey } });
      await reload();
    } catch {
      // hors ligne : la dismissal locale suffit pour la session
    }
  };
  return { visible: Boolean(me) && !seen && !dismissed, dismiss };
}

/** Aide persistante : l'encart disparaît, mais cette feuille reste réouvrable. */
export function PageHelpButton({ title, children }: {
  title: string;
  children: ReactNode;
}): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="ghost" className="page-help-button" onClick={() => setOpen(true)}>
        <HelpCircle size={18} aria-hidden="true" /> Comprendre cette page
      </Button>
      {open ? (
        <Overlay label={title} onClose={() => setOpen(false)} variant="feuille" swipeDismiss>
          <div className="sheet-handle" aria-hidden="true" />
          <div className="stack page-help">
            <span className="dashboard-kicker">Aide contextuelle</span>
            <h2>{title}</h2>
            <div>{children}</div>
            <Button variant="secondary" onClick={() => setOpen(false)}>J’ai compris</Button>
          </div>
        </Overlay>
      ) : null}
    </>
  );
}
